import type { PhotoFillResizeRect } from './types/photo-fill-resize-rect'
import type { PhotoFillResizeVisualRect } from './types/photo-fill-resize-visual-rect'

/**
 * Re-derives a photo fill's pan offset and zoom (`ShapePhotoFill.offsetX/offsetY/scale`) after
 * its frame has been resized, matching legacy `page-editor`'s architecture exactly:
 *
 * - **Corner-drag** (`isCornerHandle`): the photo scales with the frame, so the pan offset
 *   scales by the same ratio too — `content.x *= wr` in legacy's `ODSprite.setSize`
 *   (`odeditorsvg.js:5174-5175`). `scale` (our own zoom) is left untouched: since the frame's
 *   aspect ratio is preserved on a corner drag, the browser's native `preserveAspectRatio="slice"`
 *   cover-fit already scales up/down in lockstep with the frame on its own, so nothing needs
 *   freezing here.
 * - **Edge/crop drag** (any other resize, including a free/aspect-mismatched one): legacy freezes
 *   the photo's own rendered size (`content.scaledWidth/scaledHeight`) and only moves the crop
 *   window around it. Our renderer has no equivalent frozen size — it re-covers the frame fresh
 *   every render from `shape.width/height` — so a resize that changes the frame's aspect ratio
 *   (e.g. a single-edge drag) silently re-zooms the photo along with it, which legacy never does.
 *   We replicate legacy's frozen size by counter-adjusting `scale` so the *effective* covered
 *   size (native cover-fit × our zoom) stays bit-for-bit constant across the resize — see
 *   `getCurrentPhotoFillCoveredSize`. With the effective size genuinely frozen, the pan offset
 *   is simply re-anchored by the frame's center shift (legacy `ResizeObjectCmd.do`,
 *   `undo2.js:274-289`) and clamped to the frame/covered-size slack so the crop window can never
 *   expose area outside the (now frozen) photo.
 *
 * The slack clamp is bound to `visualRect` (the shape's tight, possibly off-center visual box —
 * see `visualRectLocalOfShape`), not the full `next` frame, for the same reason
 * `computeCropPhotoPan` clamps against it: a non-rectangular shape only needs the photo to cover
 * what's actually visible once clipped, not the (invisible, clipped-away) corners of its
 * rectangular bounding box. Omit it (or pass the full frame) to fall back to the old whole-frame
 * behavior — exact for a plain rectangle, where the two coincide. Unlike `computeCropPhotoPan`,
 * this does not account for `photoFill.rotation` — resize doesn't need to (the photo's rotation
 * is untouched by a frame resize), so there's no equivalent rotated-bounding-box step here.
 *
 * Callers must independently cap the resize geometry itself with `clampCropResizeToCoveredSize`
 * for edge/crop drags — this function only adjusts `photoFill`, not the shape's own
 * x/y/width/height.
 */
export function computePhotoFillOffsetAfterResize(params: {
  isCornerHandle: boolean
  initial: PhotoFillResizeRect
  next: PhotoFillResizeRect
  offsetX: number
  offsetY: number
  scale: number
  naturalWidth?: number | null
  naturalHeight?: number | null
  visualRect?: PhotoFillResizeVisualRect
}): { offsetX: number; offsetY: number; scale: number } {
  const { isCornerHandle, initial, next, offsetX, offsetY, scale, naturalWidth, naturalHeight } =
    params

  if (!(initial.width > 0) || !(initial.height > 0)) {
    return { offsetX, offsetY, scale }
  }

  if (isCornerHandle) {
    const widthRatio = next.width / initial.width
    const heightRatio = next.height / initial.height

    return { offsetX: offsetX * widthRatio, offsetY: offsetY * heightRatio, scale }
  }

  const hasNaturalSize =
    naturalWidth != null &&
    naturalHeight != null &&
    naturalWidth > 0 &&
    naturalHeight > 0 &&
    next.width > 0 &&
    next.height > 0

  let nextScale = scale

  if (hasNaturalSize) {
    const nativeCoverScaleInitial = Math.max(
      initial.width / naturalWidth,
      initial.height / naturalHeight
    )
    const nativeCoverScaleNext = Math.max(next.width / naturalWidth, next.height / naturalHeight)

    if (nativeCoverScaleNext > 0) {
      // Counteract the browser's own re-cover so the effective (native cover × our zoom)
      // rendered size stays frozen at whatever it was before this resize — legacy's equivalent
      // of never touching `content.scaledWidth/scaledHeight` during a crop-only resize.
      nextScale = scale * (nativeCoverScaleInitial / nativeCoverScaleNext)
    }

    // Safety floor: `nativeCoverScaleNext` already guarantees full coverage on its own at
    // scale=1 (that's the definition of a cover-fit ratio), so scale must never drop below 1 —
    // doing so would zoom the photo out *below* native cover and expose empty space past its
    // edges, no matter how the offset is clamped. This is a hard invariant, not just the
    // common case: if `next` was already correctly capped by `clampCropResizeToCoveredSize`
    // this is a no-op, but it also self-heals a shape whose persisted scale/size fell out of
    // sync some other way (e.g. through an earlier bug, or a still-unresolved gap elsewhere).
    nextScale = Math.max(nextScale, 1)
  }

  const initialCenterX = initial.x + initial.width / 2
  const initialCenterY = initial.y + initial.height / 2
  const nextCenterX = next.x + next.width / 2
  const nextCenterY = next.y + next.height / 2

  let reanchoredX = offsetX + (initialCenterX - nextCenterX)
  let reanchoredY = offsetY + (initialCenterY - nextCenterY)

  if (hasNaturalSize) {
    const nativeCoverScaleNext = Math.max(next.width / naturalWidth, next.height / naturalHeight)
    const coveredWidth = naturalWidth * nativeCoverScaleNext * nextScale
    const coveredHeight = naturalHeight * nativeCoverScaleNext * nextScale
    const halfCoveredW = coveredWidth / 2
    const halfCoveredH = coveredHeight / 2
    const visualRect = params.visualRect ?? { x: 0, y: 0, width: next.width, height: next.height }
    const pivotX = next.width / 2
    const pivotY = next.height / 2
    const minX = visualRect.x
    const maxX = visualRect.x + visualRect.width
    const minY = visualRect.y
    const maxY = visualRect.y + visualRect.height

    // Same "covered rect (centered at pivot + offset) must contain [minX,maxX] x [minY,maxY]"
    // bound `computeCropPhotoPan` solves — see its doc comment for the derivation. No rotation
    // step here (see this function's own doc comment for why).
    const minOffsetX = maxX - pivotX - halfCoveredW
    const maxOffsetX = minX - pivotX + halfCoveredW
    const minOffsetY = maxY - pivotY - halfCoveredH
    const maxOffsetY = minY - pivotY + halfCoveredH

    // Degenerate case (covered rect smaller than what needs covering on this axis): the solved
    // range inverts. Clamp to its midpoint — for the symmetric (rectangle) case that's exactly 0,
    // matching the previous hard-coded "no pan possible" behavior.
    const clampToRange = (raw: number, lo: number, hi: number): number =>
      lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, raw))

    reanchoredX = clampToRange(reanchoredX, minOffsetX, maxOffsetX)
    reanchoredY = clampToRange(reanchoredY, minOffsetY, maxOffsetY)
  }

  // Normalize away any -0 produced by the clamp (e.g. Math.min(0, Math.max(-0, ...))) so callers
  // never see a sign-flipped zero.
  return { offsetX: reanchoredX + 0, offsetY: reanchoredY + 0, scale: nextScale }
}
